import React, { useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import * as THREE from 'three';
import { orbActions, OrbAction } from './SentientOrbActions.tsx';

interface SentientOrbProps {
  color?: string;
  action?: OrbAction;
  actionIntensity?: number;
  onAction?: () => void;
  scale?: number;
}

export interface SentientOrbRef {
  setAction: (action: OrbAction) => void;
  setActionIntensity: (intensity: number) => void;
  triggerPulse: () => void; // New method to trigger a pulse
}

const SentientOrb = forwardRef<SentientOrbRef, SentientOrbProps>(
  ({ color, action = 'idle', actionIntensity = 1, onAction, scale = 1 }, ref) => {
    const mountRef = useRef<HTMLDivElement>(null);
    
    // Determine color based on theme
    const getThemeColor = () => {
      const theme = document.documentElement.getAttribute('data-theme');
      if (theme === 'dark') {
        return '#E8E5DA';
      }
      return color || '#00d9ff';
    };
    
    const [orbColor, setOrbColor] = React.useState(getThemeColor());
    
    // Update color when theme changes
    useEffect(() => {
      const updateColor = () => {
        setOrbColor(getThemeColor());
      };
      
      // Watch for theme changes
      const observer = new MutationObserver(updateColor);
      observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
      
      return () => observer.disconnect();
    }, [color]);
    
    // Use refs to avoid recreating scene
    const currentActionRef = useRef<OrbAction>(action);
    const actionIntensityRef = useRef(actionIntensity);
    const lastPulseTimeRef = useRef<number>(performance.now() - 10000); // Initialize to 10 seconds ago

    // Expose methods to parent
    useImperativeHandle(ref, () => ({
      setAction: (newAction: OrbAction) => {
        currentActionRef.current = newAction;
      },
      setActionIntensity: (intensity: number) => {
        actionIntensityRef.current = intensity;
      },
      triggerPulse: () => {
        lastPulseTimeRef.current = performance.now(); // Record pulse time
      }
    }));

    // Sync with props
    useEffect(() => {
      currentActionRef.current = action;
    }, [action]);

    useEffect(() => {
      actionIntensityRef.current = actionIntensity;
    }, [actionIntensity]);

    useEffect(() => {
      if (!mountRef.current) return;
      
      // Scene setup
      const scene = new THREE.Scene();
      
      const camera = new THREE.PerspectiveCamera(
        60,
        mountRef.current.clientWidth / mountRef.current.clientHeight,
        0.1,
        1000
      );
      camera.position.z = 8 / scale;
      
      const renderer = new THREE.WebGLRenderer({ 
        antialias: true, 
        alpha: true,
        powerPreference: 'high-performance'
      });
      renderer.setSize(mountRef.current.clientWidth, mountRef.current.clientHeight);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      mountRef.current.appendChild(renderer.domElement);
      
      // Convert color
      const baseColor = new THREE.Color(orbColor);
      
      // Create particle sphere
      const particleCount = 18000;
      const positions = new Float32Array(particleCount * 3);
      const originalPositions = new Float32Array(particleCount * 3);
      const distances = new Float32Array(particleCount);
      const sizes = new Float32Array(particleCount);
      const alphas = new Float32Array(particleCount);
      
      const radius = 2.5;
      const phi = Math.PI * (3 - Math.sqrt(5));
      
      for (let i = 0; i < particleCount; i++) {
        const i3 = i * 3;
        
        const y = 1 - (i / (particleCount - 1)) * 2;
        const radiusAtY = Math.sqrt(1 - y * y);
        const theta = phi * i;
        
        const x = Math.cos(theta) * radiusAtY;
        const z = Math.sin(theta) * radiusAtY;
        
        positions[i3] = x * radius;
        positions[i3 + 1] = y * radius;
        positions[i3 + 2] = z * radius;
        
        originalPositions[i3] = x * radius;
        originalPositions[i3 + 1] = y * radius;
        originalPositions[i3 + 2] = z * radius;
        
        distances[i] = Math.sqrt(x * x + y * y + z * z);
        sizes[i] = 0.04 + Math.random() * 0.02;
        alphas[i] = 0.6 + Math.random() * 0.4;
      }
      
      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));
      geometry.setAttribute('alpha', new THREE.BufferAttribute(alphas, 1));
      
      // Shaders
      const vertexShader = `
        attribute float size;
        attribute float alpha;
        varying float vAlpha;
        
        void main() {
          vAlpha = alpha;
          vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
          gl_PointSize = size * 300.0 / -mvPosition.z;
          gl_Position = projectionMatrix * mvPosition;
        }
      `;
      
      const fragmentShader = `
        uniform vec3 color;
        varying float vAlpha;
        
        void main() {
          float dist = length(gl_PointCoord - vec2(0.5));
          if (dist > 0.5) discard;
          
          float strength = 1.0 - (dist * 2.0);
          strength = pow(strength, 3.0);
          
          vec3 finalColor = color;
          gl_FragColor = vec4(finalColor, strength * vAlpha);
        }
      `;
      
      const material = new THREE.ShaderMaterial({
        uniforms: {
          color: { value: baseColor }
        },
        vertexShader,
        fragmentShader,
        transparent: true,
        blending: THREE.AdditiveBlending,
        depthWrite: false
      });
      
      const particles = new THREE.Points(geometry, material);
      scene.add(particles);
      
      // Lighting
      const ambientLight = new THREE.AmbientLight(0x222244, 0.5);
      scene.add(ambientLight);
      
      const pointLight1 = new THREE.PointLight(baseColor, 2, 50);
      pointLight1.position.set(0, 0, 0);
      scene.add(pointLight1);
      
      const pointLight2 = new THREE.PointLight(baseColor, 0.5, 50);
      pointLight2.position.set(5, 5, 5);
      scene.add(pointLight2);
      
      // Animation variables
      let time = 0;
      let mouseX = 0;
      let mouseY = 0;
      
      const handleMouseMove = (e: MouseEvent) => {
        mouseX = (e.clientX / window.innerWidth) * 2 - 1;
        mouseY = -(e.clientY / window.innerHeight) * 2 + 1;
      };
      
      window.addEventListener('mousemove', handleMouseMove);
      
      const handleClick = () => {
        if (onAction) onAction();
      };
      
      if (mountRef.current) {
        mountRef.current.addEventListener('click', handleClick);
      }
      
      // Animation loop
      const animate = () => {
        requestAnimationFrame(animate);
        time += 0.008;
        
        // Smooth rotation
        particles.rotation.y = time * 0.15;
        particles.rotation.x = Math.sin(time * 0.3) * 0.1;
        
        // Mouse interaction
        const targetX = mouseX * 0.3;
        const targetY = mouseY * 0.3;
        
        particles.rotation.y += (targetX - particles.rotation.y) * 0.05;
        particles.rotation.x += (targetY - particles.rotation.x) * 0.05;
        
        // Apply current action
        const currentAction = currentActionRef.current;
        const actionFunction = orbActions[currentAction];
        
        // Debug log (will only show every 120 frames to avoid spam)
        if (Math.floor(time * 100) % 120 === 0) {
        }
        
        if (actionFunction) {
          actionFunction({
            time,
            particleCount,
            positions,
            originalPositions,
            distances,
            geometry,
            pointLight1,
            intensity: actionIntensityRef.current
          });
        } else {
        }
        
        renderer.render(scene, camera);
      };
      
      animate();
      
      // Handle resize
      const handleResize = () => {
        if (!mountRef.current) return;
        camera.aspect = mountRef.current.clientWidth / mountRef.current.clientHeight;
        camera.updateProjectionMatrix();
        renderer.setSize(mountRef.current.clientWidth, mountRef.current.clientHeight);
      };
      
      window.addEventListener('resize', handleResize);
      
      // Cleanup
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('resize', handleResize);
        if (mountRef.current) {
          mountRef.current.removeEventListener('click', handleClick);
          if (mountRef.current.contains(renderer.domElement)) {
            mountRef.current.removeChild(renderer.domElement);
          }
        }
        geometry.dispose();
        material.dispose();
        renderer.dispose();
      };
    }, [orbColor, onAction, scale]);
    
    return (
      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div 
          ref={mountRef} 
          style={{ width: '100%', height: '100%', cursor: onAction ? 'pointer' : 'default' }}
        />
      </div>
    );
  }
);

SentientOrb.displayName = 'SentientOrb';

export default SentientOrb;